<template>
    <div class="betSlip _singleTicket notranslate" data-bet-slip-scroll-boundary>
        <JackpotWidget v-if="isShowJackpotWidget" v-bind:showCloseButton="false" />
        <div class="betSlip-area">
            <div v-bind:class="{ _stakePadShow: !isShowStakePad }" class="betSlip-scrollContent">
                <EuroDynamicMarketTicket />
                <Message
                    v-if="betSlipMessage"
                    status="error"
                    v-bind:message="betSlipMessage"
                    v-bind:duration="3000"
                    position="bottom"
                    v-on:clearMessage="setBetSlipMessage(null)"
                />
                <SwitchVoucherRedeem
                    v-if="isVoucherAvailable"
                    gaClassPrefix="SingleSlip"
                    v-bind:isRedeemVoucher="isRedeemVoucher"
                    v-on:toggleRedeemVoucher="setIsRedeemVoucher"
                />
                <div class="ticket-maxPayout">
                    <span>{{ getMaxPayoutText }}</span>
                    <span class="notranslate ticket-amount"> {{ maxPayout }}</span>
                </div>
                <div class="ticket-inputArea">
                    <PlaceBet
                        v-if="!isRedeemVoucher"
                        v-bind:stake="stake"
                        v-bind:isDisableBtn="isDisablePlaceBet"
                        v-bind:balance="balance"
                        v-bind:minBet="betSlip.minBet"
                        v-bind:price="price"
                        v-on:placeBet="placeBet"
                        v-on:changeStake="setStake"
                    />
                    <StakePad
                        v-if="!isRedeemVoucher"
                        v-bind:stake="stake"
                        v-bind:balance="balance"
                        v-bind:minBet="minBet"
                        v-bind:maxBet="maxBet"
                        v-bind:currency="customerInfo.currency"
                        v-bind:price="price"
                        v-on:changeStake="setStake"
                    />
                    <PlaceVoucherBet
                        v-if="isRedeemVoucher"
                        v-bind:voucherList="voucherList"
                        v-bind:isDisableBtn="isDisablePlaceBet"
                        v-on:updateSelectedVoucher="updateSelectedVoucher"
                        v-on:placeVoucherBet="placeBet"
                    />
                </div>
                <SwitchAcceptAnyOddsValue
                    v-if="isShowAcceptAnyOdds"
                    class="betSlip-acceptAnyOdds"
                />
            </div>
        </div>
    </div>
</template>

<script lang="ts">
    import { storeToRefs } from 'pinia';
    import { computed, defineComponent, onMounted, onUnmounted, watch } from 'vue';
    import JackpotWidget from '../jackpot/JackpotWidget.vue';
    import SwitchAcceptAnyOddsValue from '@/components/SwitchAcceptAnyOddsValue.vue';
    import EuroDynamicMarketTicket from '@/components/betSlip/EuroDynamicMarketTicket.vue';
    import PlaceBet from '@/components/betSlip/PlaceBet.vue';
    import PlaceVoucherBet from '@/components/betSlip/PlaceVoucherBet.vue';
    import StakePad from '@/components/betSlip/StakePad.vue';
    import Message from '@/components/common/SimpleMessage.vue';
    import SwitchVoucherRedeem from '@/components/voucher/SwitchVoucherRedeem.vue';
    import type { IVoucherDisplay } from '@/interface/voucher';
    import { isBalanceLessMinBet } from '@/libraries/betSlip/stake';
    import { useJackpotDisplay } from '@/libraries/composables/useJackpotDisplay';
    import { i18n } from '@/libraries/i18n';
    import { enableScrolling } from '@/libraries/utils/dom';
    import { useAccountStore } from '@/stores/accountStore';
    import { useBetSlipStore } from '@/stores/betSlip/betSlipStore';
    import { useDynamicMarketBetSlipStore } from '@/stores/betSlip/dynamicMarketBetSlipStore';

    export default defineComponent({
        name: 'DynamicMarketBetSlip',
        components: {
            EuroDynamicMarketTicket,
            SwitchAcceptAnyOddsValue,
            JackpotWidget,
            Message,
            PlaceBet,
            StakePad,
            SwitchVoucherRedeem,
            PlaceVoucherBet,
        },
        setup() {
            const { isShowJackpotWidget } = useJackpotDisplay();
            const { isShowStakePad } = storeToRefs(useBetSlipStore());
            const { setIsShowStakePad } = useBetSlipStore();
            const { customerInfo, balance } = storeToRefs(useAccountStore());

            const isShowAcceptAnyOdds = computed(() => !customerInfo.value.isAcceptAnyOdds);

            const {
                voucherList, isRedeemVoucher, stake, betSlip, isPlacingBet, isValidStake, selectedVoucher, minBet, maxBet, betSlipMessage, maxPayout, price,
            } = storeToRefs(useDynamicMarketBetSlipStore());
            const { setIsRedeemVoucher, placeBet, setStake, openTicket, setBetSlipMessage } = useDynamicMarketBetSlipStore();
            const isVoucherAvailable = computed(() => voucherList.value.some(voucher => voucher.isAvailable));

            const isValidBetSetting = computed(() => betSlip.value.minBet > 0 && betSlip.value.maxBet > 0);

            const isDisablePlaceBet = computed(() => {
                if (!isValidBetSetting.value) return true;
                if (isPlacingBet.value) return true;
                if (isRedeemVoucher.value) return !isValidStake.value;
                if (isBalanceLessMinBet(balance.value, betSlip.value.minBet, betSlip.value.selection?.price?.value ?? 0)) return true;

                if (balance.value === null) return false;

                return Math.floor(balance.value) <= 0;
            });

            const getMaxPayoutText = computed(() => i18n.t('maxPayout'));

            function updateSelectedVoucher(voucher: IVoucherDisplay | null) {
                selectedVoucher.value = voucher;
            }

            onUnmounted(() => {
                enableScrolling();
            });

            onMounted(async () => {
                await openTicket(true);
            });

            watch(() => betSlip.value.uid, async () => {
                if (!betSlip.value.uid) {
                    await openTicket(true);
                }
            });

            setIsShowStakePad(true);

            return {
                isShowJackpotWidget,
                isShowStakePad,
                isShowAcceptAnyOdds,
                isVoucherAvailable,
                isRedeemVoucher,
                setIsRedeemVoucher,
                customerInfo,
                stake,
                isDisablePlaceBet,
                placeBet,
                setStake,
                selectedVoucher,
                voucherList,
                balance,
                minBet,
                maxBet,
                betSlip,
                betSlipMessage,
                getMaxPayoutText,
                maxPayout,
                price,
                setBetSlipMessage,
                updateSelectedVoucher,
            };
        },
    });
</script>

<style lang="scss" scoped>
@import "@/scss/components/betSlip";
@import "@/scss/components/ticket";
</style>
